/**
 * Centralized Image Utilities
 * 
 * Provides consistent image optimization, upload, and URL generation
 * across the entire application.
 */

// PERF: NO static supabase import. OptimizedImage (rendered on every marketing
// page) imports the pure URL helpers from this file, and a static supabase
// chain here bundled supabase-js (~64KB gz) into every page's first load.
// Only the admin-side upload/delete functions need supabase — they load it
// on demand.
const loadSupabase = async () => (await import('@/lib/supabaseClient')).supabase;

// Bucket configuration
export const IMAGE_BUCKETS = {
  BLOG: 'blog-images',
  FEATURES: 'feature-images',
  AVATARS: 'avatars',
  MARKETING: 'marketing-images',
} as const;

// Image optimization settings
export const IMAGE_CONFIG = {
  MAX_WIDTH: 1200,
  QUALITY: 85,
  AVATAR_QUALITY: 90,
  FORMAT: 'webp',
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  MAX_DIMENSIONS: 4096, // 4K max
  ALLOWED_FORMATS: ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif'],
} as const;

// Responsive breakpoints for srcset generation
export const RESPONSIVE_BREAKPOINTS = [640, 1024, 1920] as const;

/**
 * Passthrough — returns the storage URL unchanged.
 *
 * We deliberately do NOT rewrite to the `/render/image/` transform endpoint anymore:
 * that endpoint only exists on the self-hosted stack (imgproxy) and 403s on the cloud
 * Supabase projects the DB still references (ivjyeidgdinkpntkaepy), which broke those
 * images. Image optimization is handled by updating the stored URLs in the DB (converting
 * files to WebP + repointing), not at render time.
 */
export function getOptimizedImageUrl(
  url: string,
  _options?: { width?: number; quality?: number }
): string {
  return url;
}

/**
 * Passthrough — returns the HTML unchanged. (Previously rewrote embedded <img> to the
 * /render/image transform endpoint; removed because that 403s on the cloud Supabase projects
 * the DB references. Image optimization is done by updating stored URLs in the DB instead.)
 */
export function optimizeContentImages(
  html: string | null | undefined,
  _options?: { width?: number; quality?: number },
): string {
  return html ?? '';
}

/**
 * Generate responsive srcset for images
 */
export function generateSrcSet(
  baseUrl: string,
  maxWidth: number,
  quality: number = IMAGE_CONFIG.QUALITY
): string | undefined {
  if (!isSupabaseStorageUrl(baseUrl)) {
    return undefined; // Only for Supabase images
  }

  const widths = RESPONSIVE_BREAKPOINTS.filter(w => w <= maxWidth * 2);

  return widths
    .map(w => `${getOptimizedImageUrl(baseUrl, { width: w, quality })} ${w}w`)
    .join(', ');
}

/**
 * Validate image file before upload
 */
export function validateImageFile(file: File): {
  valid: boolean;
  error?: string;
} {
  // Check file type
  if (!IMAGE_CONFIG.ALLOWED_FORMATS.includes(file.type as typeof IMAGE_CONFIG.ALLOWED_FORMATS[number])) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${IMAGE_CONFIG.ALLOWED_FORMATS.join(', ')}`,
    };
  }

  // Check file size
  if (file.size > IMAGE_CONFIG.MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${formatFileSize(IMAGE_CONFIG.MAX_FILE_SIZE)}`,
    };
  }

  return { valid: true };
}

/**
 * Convert image to WebP format with optimization
 */
export async function convertToWebP(
  file: File | Blob,
  maxWidth: number = IMAGE_CONFIG.MAX_WIDTH,
  quality: number = IMAGE_CONFIG.QUALITY
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    img.onload = () => {
      // Calculate dimensions
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and convert
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to convert image'));
          }
        },
        'image/webp' as 'image/webp',
        quality / 100
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Upload and optimize image to Supabase Storage
 */
export async function uploadOptimizedImage(
  file: File | Blob,
  bucket: string,
  path?: string,
  options?: {
    maxWidth?: number;
    quality?: number;
    shouldConvertToWebP?: boolean;
  }
): Promise<{ url: string; path: string }> {
  const {
    maxWidth = IMAGE_CONFIG.MAX_WIDTH,
    quality = IMAGE_CONFIG.QUALITY,
    shouldConvertToWebP = true,
  } = options || {};

  // Validate if it's a File
  if (file instanceof File) {
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }
  }

  // Convert to WebP if requested
  let uploadFile = file;
  let fileExtension = 'webp';

  if (shouldConvertToWebP && file.type !== 'image/webp') {
    uploadFile = await convertToWebP(file, maxWidth, quality);
  } else if (file instanceof File) {
    fileExtension = file.name.split('.').pop() || 'webp';
  }

  // Generate file path
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(7);
  const filePath = path || `${timestamp}-${randomStr}.${fileExtension}`;

  // Upload to Supabase Storage (client loaded on demand — admin-only path)
  const supabase = await loadSupabase();
  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(filePath, uploadFile, {
      upsert: true,
      contentType: fileExtension === 'svg' ? 'image/svg+xml' : `image/${fileExtension}`,
    });

  if (uploadError) throw uploadError;

  // Get public URL
  const {
    data: { publicUrl },
  } = supabase.storage.from(bucket).getPublicUrl(filePath);

  return { url: publicUrl, path: filePath };
}

/**
 * Delete image from Supabase Storage
 */
export async function deleteImage(
  bucket: string,
  path: string
): Promise<void> {
  const supabase = await loadSupabase();
  const { error } = await supabase.storage.from(bucket).remove([path]);

  if (error) throw error;
}

/**
 * Format file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Generate avatar placeholder URL using ui-avatars.com
 */
export function getAvatarPlaceholder(
  name: string,
  options?: {
    size?: number;
    background?: string;
    color?: string;
  }
): string {
  const {
    size = 128,
    background = 'random',
    color = 'fff',
  } = options || {};

  const params = new URLSearchParams({
    name: name,
    size: size.toString(),
    background: background,
    color: color,
    bold: 'true',
  });

  return `https://ui-avatars.com/api/?${params.toString()}`;
}

/**
 * Check if URL is a Supabase Storage URL
 */
export function isSupabaseStorageUrl(url: string): boolean {
  return url.includes('/storage/v1/object/public/');
}

/**
 * Extract bucket and path from Supabase Storage URL
 */
export function parseSupabaseUrl(
  url: string
): { bucket: string; path: string } | null {
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    const bucketIndex = pathParts.indexOf('object') + 2; // /storage/v1/object/public/{bucket}/{path}

    if (bucketIndex < 2) return null;

    const bucket = pathParts[bucketIndex];
    const path = pathParts.slice(bucketIndex + 1).join('/');

    return { bucket, path };
  } catch {
    return null;
  }
}
